// frontend/composables/useExtraPacksStore.ts
/**
 * Extra Packs Store Composable
 * 
 * Provides a centralized, route-aware way to fetch and manage extra packs data
 * to prevent redundant API calls in contexts where the data isn't needed.
 * 
 * IMPORTANT: This store should ONLY be initialized on these specific routes:
 * - /extra-packs
 * - /account/dashboard?view=extra-roaming
 * 
 * GraphQL queries will only execute when on these routes to optimize performance.
 */

import { ref, computed } from 'vue';
import { useStrapiService } from './services/useStrapi.service';
import { useMainStore } from '~/stores/main';
import { useRoute } from 'vue-router';
import type { ExtraPackType, ExtraPackMoreType } from '~/types';

export const useExtraPacksStore = () => {
  const { getExtraPacks, getExtraPacksMore } = useStrapiService();
  const mainStore = useMainStore();
  const route = useRoute();
  
  // Debug logging utility - only logs in non-production environments
  const debugLog = (message: string, data?: any) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[ExtraPacksStore] ${message}`, data || '');
    }
  };
  
  // State
  const extraPacks = ref<ExtraPackType[] | null>(null);
  const extraPacksMore = ref<ExtraPackMoreType[] | null>(null);
  const loading = ref(false);
  const error = ref<Error | null>(null);
  const lastFetchTime = ref<Record<string, number>>({
    extraPacks: 0,
    extraPacksMore: 0
  });
  
  // TTL for data (30 minutes)
  const CACHE_TTL = 30 * 60 * 1000;
  
  // Check if we're in an appropriate context to load extra packs
  const isRelevantContext = computed(() => {
    // Always relevant in extra-packs section
    if (route.path.startsWith('/extra-packs')) {
      return true;
    }
    
    // Only relevant in dashboard if on the extra-roaming view
    if (route.path.startsWith('/account/dashboard')) {
      return route.query.view === 'extra-roaming';
    }
    
    return false;
  });
  
  // Computed getter to check if data is stale based on TTL
  const isStale = computed(() => {
    const now = Date.now();
    const packsStale = !lastFetchTime.value.extraPacks || 
                       (now - lastFetchTime.value.extraPacks) > CACHE_TTL;
    const moreStale = !lastFetchTime.value.extraPacksMore || 
                     (now - lastFetchTime.value.extraPacksMore) > CACHE_TTL;
    
    return {
      extraPacks: packsStale,
      extraPacksMore: moreStale,
      any: packsStale || moreStale
    };
  });
  
  /**
   * Get basic extra packs 
   * @param forceFresh Force a fresh request ignoring cache
   */
  const getBasicExtras = async (forceFresh = false): Promise<ExtraPackType[] | null> => {
    // Skip if not in a relevant context
    if (!isRelevantContext.value && !forceFresh) {
      debugLog('Skipping extra packs fetch - not in relevant context', { 
        route: route.path, 
        query: route.query 
      });
      return extraPacks.value || mainStore.extraPacks;
    }
    
    // Return cached data if fresh
    if (!forceFresh && 
        !isStale.value.extraPacks && 
        (extraPacks.value || mainStore.extraPacks)) {
      debugLog('Using cached extra packs data', { 
        cacheAge: Date.now() - (lastFetchTime.value.extraPacks || 0),
        itemCount: extraPacks.value?.length || mainStore.extraPacks?.length
      });
      return extraPacks.value || mainStore.extraPacks;
    }
    
    // Avoid concurrent requests
    if (loading.value) {
      debugLog('Extra packs fetch already in progress - waiting for completion');
      await new Promise<void>(resolve => {
        const checkLoading = () => {
          if (!loading.value) {
            resolve();
          } else {
            setTimeout(checkLoading, 50);
          }
        };
        checkLoading();
      });
      return extraPacks.value;
    }
    
    loading.value = true;
    
    try {
      debugLog('Fetching extra packs data...', {
        route: route.path,
        view: route.query.view,
        forceFresh
      });
      const result = await getExtraPacks();
      
      if (result) {
        extraPacks.value = result;
        lastFetchTime.value.extraPacks = Date.now();
        
        // Also update the main store
        mainStore.setExtraPacks(result);
        debugLog('Successfully fetched extra packs data', { count: result.length });
      }
      
      return result;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      debugLog('Error fetching extra packs', { error: errorMsg });
      error.value = err instanceof Error ? err : new Error(String(err));
      return null;
    } finally {
      loading.value = false;
    }
  };
  
  /**
   * Get detailed extra packs information
   * @param forceFresh Force a fresh request ignoring cache
   */
  const getDetailedExtras = async (forceFresh = false): Promise<ExtraPackMoreType[] | null> => {
    // Skip if not in a relevant context
    if (!isRelevantContext.value && !forceFresh) {
      debugLog('Skipping detailed extra packs fetch - not in relevant context', { 
        route: route.path, 
        query: route.query 
      });
      return extraPacksMore.value || mainStore.extraPacksMore;
    }
    
    // Return cached data if fresh
    if (!forceFresh && 
        !isStale.value.extraPacksMore && 
        (extraPacksMore.value || mainStore.extraPacksMore)) {
      debugLog('Using cached detailed extra packs data', { 
        cacheAge: Date.now() - (lastFetchTime.value.extraPacksMore || 0),
        itemCount: extraPacksMore.value?.length || mainStore.extraPacksMore?.length
      });
      return extraPacksMore.value || mainStore.extraPacksMore;
    }
    
    // Avoid concurrent requests
    if (loading.value) {
      debugLog('Detailed extra packs fetch already in progress - waiting for completion');
      await new Promise<void>(resolve => {
        const checkLoading = () => {
          if (!loading.value) {
            resolve();
          } else {
            setTimeout(checkLoading, 50);
          }
        };
        checkLoading();
      });
      return extraPacksMore.value;
    }
    
    loading.value = true;
    
    try {
      debugLog('Fetching detailed extra packs data...', {
        route: route.path,
        view: route.query.view,
        forceFresh
      });
      const result = await getExtraPacksMore();
      
      if (result) {
        extraPacksMore.value = result;
        lastFetchTime.value.extraPacksMore = Date.now();
        
        // Also update the main store
        mainStore.setExtraPacksMore(result);
        debugLog('Successfully fetched detailed extra packs data', { count: result.length });
      }
      
      return result;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      debugLog('Error fetching detailed extra packs', { error: errorMsg });
      error.value = err instanceof Error ? err : new Error(String(err));
      return null;
    } finally {
      loading.value = false;
    }
  };
  
  /**
   * Get all extra packs data (both basic and detailed)
   * @param forceFresh Force a fresh request ignoring cache
   */
  const getAllExtras = async (forceFresh = false) => {
    if (!isRelevantContext.value && !forceFresh) {
      debugLog('Skipping all extras fetch - not in relevant context', {
        route: route.path,
        query: route.query
      });
      return {
        extraPacks: extraPacks.value || mainStore.extraPacks,
        extraPacksMore: extraPacksMore.value || mainStore.extraPacksMore
      };
    }
    
    debugLog('Fetching all extra packs data', {
      route: route.path,
      view: route.query.view,
      forceFresh
    });
    
    const [packsResult, moreResult] = await Promise.all([
      getBasicExtras(forceFresh),
      getDetailedExtras(forceFresh)
    ]);
    
    debugLog('Completed fetching all extra packs data', {
      basicCount: packsResult?.length || 0,
      detailedCount: moreResult?.length || 0
    });
    
    return {
      extraPacks: packsResult,
      extraPacksMore: moreResult
    };
  };
  
  /**
   * Initialize the store by checking the context and loading if needed
   * 
   * @param forceInitialize Force initialization even if not in relevant context
   * @returns Promise resolving to boolean indicating if initialization occurred
   */
  const initialize = async (forceInitialize = false): Promise<boolean> => {
    // Only initialize if we're in a relevant context OR explicitly forced
    if (isRelevantContext.value || forceInitialize) {
      debugLog('Initializing extra packs store', {
        route: route.path,
        query: route.query,
        forced: forceInitialize,
        relevantContext: isRelevantContext.value
      });
      
      // Use mainStore data if available
      if (mainStore.extraPacks) {
        extraPacks.value = mainStore.extraPacks;
        debugLog('Using main store data for basic extra packs', { count: mainStore.extraPacks.length });
      }
      
      if (mainStore.extraPacksMore) {
        extraPacksMore.value = mainStore.extraPacksMore;
        debugLog('Using main store data for detailed extra packs', { count: mainStore.extraPacksMore.length });
      }
      
      // If data is stale or missing, fetch fresh data
      if (isStale.value.any) {
        debugLog('Data is stale or missing, fetching fresh data');
        await getAllExtras(false);
      }
      
      return true;
    } else {
      debugLog('Skipping extra packs store initialization - not in relevant context', {
        route: route.path,
        query: route.query
      });
      return false;
    }
  };
  
  return {
    // State
    extraPacks: computed(() => extraPacks.value || mainStore.extraPacks),
    extraPacksMore: computed(() => extraPacksMore.value || mainStore.extraPacksMore),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    isRelevantContext,
    isStale,
    
    // Methods
    getBasicExtras,
    getDetailedExtras,
    getAllExtras,
    initialize
  };
};
